/**
 * The workspace theme (Settings → Brand) as the inline variables the viewer
 * pages read. The accent and the secondary re-skin the wine and brass; the
 * paper is the ground, but only for the mode it belongs to: a light paper is
 * the light mode's ground and a dark paper the dark mode's, so a workspace
 * whose paper is dark (Flashkut's is) never leaves light mode with dark ink on
 * a dark page. The other mode keeps the page's own ground.
 */
import type { CSSProperties } from "react";
import { inkOn, luminanceOf, mixHex, normalHex, readableOn } from "../../shared/brand-colors";
import type { PublicBrand } from "../../shared/types";

export { luminanceOf };

/** Below this the paper is a dark ground: mid-greys and up read as light. */
const DARK_BELOW = 0.35;

/**
 * The greys of the page, each moved a little toward the accent. The page was
 * drawn in greys that carried wine in them (a wine-black ground, wine-grey
 * hairlines, a pink for dark-mode links), so a blue brand's Room still read as
 * wine around the edges. The same greys, carrying the brand instead.
 */
function tintedPalette(accent: string): Record<string, string> {
  const toward = (base: string, keep: number) => mixHex(accent, base, keep);
  return {
    "--brand-ink": toward("#1a1a1e", 0.92),
    "--brand-muted": toward("#66666e", 0.9),
    "--brand-dim": toward("#97979f", 0.9),
    "--brand-surface-2": toward("#efeff2", 0.94),
    "--brand-hairline": toward("#e4e4e9", 0.93),
    "--brand-dark-ground": toward("#0f0f12", 0.94),
    "--brand-dark-surface": toward("#18181c", 0.93),
    "--brand-dark-surface-2": toward("#212127", 0.92),
    "--brand-dark-hairline": toward("#2c2c33", 0.9),
    "--brand-dark-player": toward("#0b0b0e", 0.95),
    "--brand-dark-ink": toward("#f0eff3", 0.96),
    "--brand-dark-muted": toward("#b1b0b8", 0.93),
    "--brand-dark-dim": toward("#84838c", 0.92),
  };
}

export function paperMode(paper: string | null | undefined): "light" | "dark" | null {
  if (!paper) return null;
  const l = luminanceOf(paper);
  if (l === null) return null;
  return l < DARK_BELOW ? "dark" : "light";
}

export function brandVars(brand: Pick<PublicBrand, "accent" | "brass" | "paper">): CSSProperties {
  const vars: Record<string, string> = {
    "--wine": brand.accent,
    "--wine-deep": brand.accent,
    "--brass": brand.brass,
    "--brand-accent-text": brand.accent,
  };
  // Words on the accent, and the accent as words on a dark picture: worked out
  // here because CSS cannot tell a pale coral from a deep wine. The offer in
  // the player reads on either.
  const accent = normalHex(brand.accent);
  if (accent) {
    vars["--brand-accent-ink"] = inkOn(accent);
    vars["--brand-accent-lit"] = readableOn(accent, "#15101a");
    Object.assign(vars, tintedPalette(accent));
  }
  const mode = paperMode(brand.paper);
  if (mode === "light") vars["--brand-paper-light"] = brand.paper;
  if (mode === "dark") vars["--brand-paper-dark"] = brand.paper;
  return vars as CSSProperties;
}
